import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { createStripeCheckoutSession } from "@/lib/stripe-payment.functions";

export const Route = createFileRoute("/betalen/$orderId")({
  head: () => ({
    meta: [
      { title: "Betalen · Elvion Group Nederland" },
      {
        name: "description",
        content: "Rond je bestelling veilig af via Stripe.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BetalenPage,
});

function BetalenPage() {
  const { orderId } = Route.useParams();
  const navigate = useNavigate();
  const createSession = useServerFn(createStripeCheckoutSession);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const stripePromise = useMemo(() => getStripe(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const env = getStripeEnvironment();
        const res = await createSession({ data: { orderId, environment: env } });
        if (cancelled) return;
        if ("error" in res) {
          setError(res.error);
          return;
        }
        if (!res.clientSecret) {
          setError("Geen client secret ontvangen van Stripe");
          return;
        }
        setClientSecret(res.clientSecret);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId, createSession]);

  return (
    <>
      <Header />
      <div className="bg-background">
        <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
          <nav className="mb-6 flex items-center gap-2 text-sm text-text-secondary">
            <Link to="/" className="hover:text-foreground">
              Home
            </Link>
            <ChevronRight className="size-3.5" />
            <Link to="/checkout" className="hover:text-foreground">
              Checkout
            </Link>
            <ChevronRight className="size-3.5" />
            <span className="font-medium text-foreground">Betalen</span>
          </nav>

          <h1 className="mb-2 text-3xl font-bold tracking-tight">
            Veilig afrekenen
          </h1>
          <p className="mb-6 flex items-center gap-2 text-sm text-text-secondary">
            <ShieldCheck className="size-4 text-emerald-600" />
            Betaling wordt beveiligd afgehandeld door Stripe (iDEAL, kaart,
            Bancontact).
          </p>

          {error ? (
            <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6">
              <h2 className="text-lg font-semibold text-destructive">
                Betaling kon niet gestart worden
              </h2>
              <p className="mt-2 text-sm text-text-secondary">{error}</p>
              <div className="mt-4 flex gap-3">
                <Button onClick={() => navigate({ to: "/checkout" })}>
                  Terug naar checkout
                </Button>
                <Button variant="outline" onClick={() => location.reload()}>
                  Opnieuw proberen
                </Button>
              </div>
            </div>
          ) : !clientSecret ? (
            <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-text-secondary">
              Betaling wordt voorbereid…
            </div>
          ) : (
            <div
              id="stripe-checkout"
              className="rounded-2xl border border-border bg-card p-2 sm:p-4"
            >
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ clientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
